import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Car, Search, CheckCircle2, AlertCircle } from "lucide-react";

// UK registration-plate lookup — auto-fills tyre size and lands on filtered list.
// Backed by /api/lookup-reg (mocked fitment today; swap-in real DVLA + TecDoc when ready).
const RegLookup = () => {
  const [plate, setPlate] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!plate.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await api.get("/lookup-reg", { params: { plate: plate.trim() } });
      setResult(r.data);
    } catch (err) {
      setError("Couldn't find that registration. Please check and try again.");
    } finally {
      setLoading(false);
    }
  };

  const useThisSize = () => {
    if (!result) return;
    const { width, profile, rim } = result.size;
    navigate(`/tyres?width=${width}&profile=${profile}&rim=${rim}`);
    // Always bring the user to the filtered results — they're below the hero
    // and otherwise the click feels like "nothing happened".
    setTimeout(() => {
      const grid = document.querySelector('[data-testid="tyres-grid"]')
        || document.querySelector('[data-testid="tyres-loading"]')
        || document.querySelector('[data-testid="tyres-empty"]')
        || document.querySelector('[data-testid="results-count"]');
      if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <div className="vd-card p-6" data-testid="reg-lookup">
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center justify-center rounded-full" style={{ width: 40, height: 40, background: "rgba(230,0,0,0.08)" }}>
          <Car size={20} color="var(--primary-red)" />
        </span>
        <div>
          <h3 style={{ fontSize: 20 }}>Find tyres by number plate</h3>
          <p className="vd-muted text-sm">Enter your UK registration — we'll find the exact fitment.</p>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
        <div className="vd-plate-wrap">
          <input
            type="text"
            value={plate}
            onChange={(e) => setPlate(e.target.value.toUpperCase())}
            placeholder="AB12 XYZ"
            maxLength={10}
            className="vd-plate-input"
            aria-label="UK registration plate"
            data-testid="reg-plate-input"
          />
        </div>
        <button type="submit" className="vd-btn-primary" disabled={loading || !plate.trim()} data-testid="reg-lookup-btn">
          <Search size={18} /> {loading ? "Searching..." : "Find My Tyres"}
        </button>
      </form>

      {error && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-md" style={{ background: "rgba(230,0,0,0.06)", border: "1px solid rgba(230,0,0,0.35)" }} data-testid="reg-error">
          <AlertCircle size={18} color="var(--primary-red)" className="mt-0.5" />
          <p className="text-sm" style={{ color: "var(--primary-red)" }}>{error}</p>
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 rounded-md" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)" }} data-testid="reg-result">
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle2 size={20} color="var(--primary-red)" className="mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="vd-heading font-bold text-lg">{result.make} {result.model}</p>
                {result.source === "dvla" && (
                  <span className="text-[10px] font-bold rounded px-1.5 py-0.5" style={{ background: "rgba(0,180,80,0.15)", color: "#00A35C" }} data-testid="reg-dvla-badge">DVLA verified</span>
                )}
              </div>
              <p className="vd-muted text-sm">
                {result.year} · {result.fuel}{result.colour ? ` · ${result.colour}` : ""}{result.engine_capacity_cc ? ` · ${result.engine_capacity_cc}cc` : ""} · Plate <span className="font-mono font-semibold vd-heading">{result.plate}</span>
              </p>
            </div>
          </div>

          {(result.mot_status || result.tax_status) && (
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="reg-mot-tax">
              {result.mot_status && (
                <div className="rounded-md p-2.5" style={{ background: "var(--card-bg)", border: "1px solid var(--border-color)" }}>
                  <p className="vd-muted text-[11px] uppercase tracking-wider">MOT</p>
                  <p className="vd-heading font-bold text-sm" style={{ color: result.mot_status === "Valid" ? "#00A35C" : "var(--primary-red)" }}>
                    {result.mot_status}
                  </p>
                  {result.mot_expiry && <p className="vd-muted text-[11px]">expires {result.mot_expiry}</p>}
                </div>
              )}
              {result.tax_status && (
                <div className="rounded-md p-2.5" style={{ background: "var(--card-bg)", border: "1px solid var(--border-color)" }}>
                  <p className="vd-muted text-[11px] uppercase tracking-wider">Road Tax</p>
                  <p className="vd-heading font-bold text-sm" style={{ color: result.tax_status === "Taxed" ? "#00A35C" : "var(--primary-red)" }}>
                    {result.tax_status}
                  </p>
                  {result.tax_due && <p className="vd-muted text-[11px]">due {result.tax_due}</p>}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="vd-muted text-xs uppercase tracking-wider mb-1">Recommended Size</p>
              <p className="vd-heading text-2xl font-extrabold" data-testid="reg-fitment-size">
                {result.size.width}/{result.size.profile} R{result.size.rim}
              </p>
            </div>
            <button onClick={useThisSize} className="vd-btn-primary" data-testid="reg-shop-this-size">Shop this size →</button>
          </div>
        </div>
      )}

      <style>{`
        .vd-plate-wrap {
          background: #F2C200;
          border: 2px solid #1a1a1a;
          border-radius: 6px;
          padding: 4px 8px;
          flex: 1;
          display: flex;
          align-items: center;
          min-width: 0;
        }
        .vd-plate-input {
          background: transparent;
          border: none;
          outline: none;
          width: 100%;
          font-family: "Helvetica Neue", Arial, sans-serif;
          font-weight: 900;
          font-size: 26px;
          letter-spacing: 0.08em;
          color: #000;
          text-align: center;
          text-transform: uppercase;
          padding: 6px 4px;
        }
        .vd-plate-input::placeholder { color: rgba(0,0,0,0.35); }
      `}</style>
    </div>
  );
};

export default RegLookup;
